<template>
  <el-row :gutter="16">
    <el-col :span="12">
      <PDatePicker
        type="date"
        v-model="date"
        :disabled="selecionarPeriodo"
        :error="!selecionarPeriodo && !!errors?.date"
        placeholder="Selecione uma data"
        label="Data"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" style="margin-top: 2px">
      <PSelect
        v-model="empresa"
        placeholder="Selecione uma empresa"
        label="Empresa"
        :options="empresasOpt"
        filterable
        clearable
      />
    </el-col>
    <el-col :span="12" style="margin-top: 16px">
      <PSelect
        v-model="tipoDePesquisa"
        placeholder="Selecione um tipo de integração"
        label="Tipo de integração"
        :options="TipoDePesquisaCadisOpt"
        filterable
        clearable
      />
    </el-col>
    <el-col :span="24" style="margin: 0.7rem 0">
      <PCheckbox label="Selecionar um período" v-model="selecionarPeriodo" />
    </el-col>
    <el-col :span="12" v-if="selecionarPeriodo" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="inicio"
        :error="selecionarPeriodo && !inicio"
        placeholder="Selecione uma data de início"
        label="Início"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" v-if="selecionarPeriodo" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="fim"
        :error="selecionarPeriodo && !fim"
        placeholder="Selecione uma data de fim"
        label="Fim"
        style="width: 100%"
        clearable
      />
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
import { DITypes } from "@/core/@types/types";
import {
  ETipoDePesquisaCadis,
  TipoDePesquisaCadisOpt
} from "../../../../../../@types/IntegracoesAPIEnum";
import { PCheckbox, PDatePicker, PSelect } from "@f360-dev/f360-prisma";
import { TWebservice } from "../../../../services/PainelDeIntegracaoesGateway";
import { TAPIRules } from "../../../../../../@types/IntegracoesAPIRules";
import { diContainer } from "@/inversify.config";
import { IFinancasFeatureGateway } from "@/features/financas/services/ports/FinancasFeatureGateway";
import { useForm } from "vee-validate";
import { computed, onMounted } from "vue";
import {
  formCadisSchema,
  IFormularioCadisSchema
} from "./formularioCadisSchema";

const props = defineProps<{
  webservice?: TWebservice;
  empresasOpt: { label: string; value: string }[];
  apiSelecionada?: TAPIRules;
}>();

const financasFeatureGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);

const { handleSubmit, errors, setValues, values, defineField } =
  useForm<IFormularioCadisSchema>({
    validationSchema: formCadisSchema,
    initialValues: {
      date: new Date().toISOString().split("T")[0],
      empresa: "",
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoDePesquisa: ETipoDePesquisaCadis.None.toString()
    }
  });

const [date] = defineField("date");
const [empresa] = defineField("empresa");
const [selecionarPeriodo] = defineField("selecionarPeriodo");
const [inicio] = defineField("inicio");
const [fim] = defineField("fim");
const [tipoDePesquisa] = defineField("tipoDePesquisa");

const periodoUltrapassaLimiteDaApi = (): boolean => {
  const limite = props.apiSelecionada?.intervaloData;
  if (limite == null || limite <= 0) return false;

  const dataInicio = inicio.value != null ? new Date(inicio.value) : null;
  const dataFim = fim.value != null ? new Date(fim.value) : null;

  if (!dataInicio || !dataFim) return false;

  const diffEmDiasInicio = dataInicio.getDate();
  const diffEmDiasFim = dataFim.getDate();
  const diffEmDias = diffEmDiasFim - diffEmDiasInicio;

  if (diffEmDias < 0) return true;

  return diffEmDias >= limite;
};

const disabledSubmit = computed(() => {
  const missingDate = !date.value || String(date.value).trim() === "";
  const missingEmpresa = !empresa.value || String(empresa.value).trim() === "";
  const missingTipoDePesquisa =
    !tipoDePesquisa.value || String(tipoDePesquisa.value).trim() === "";

  if (missingDate || missingEmpresa || missingTipoDePesquisa) return true;

  if (selecionarPeriodo.value) {
    const missingInicio =
      inicio.value == null || String(inicio.value).trim() === "";
    const missingFim = fim.value == null || String(fim.value).trim() === "";

    return missingInicio || missingFim || periodoUltrapassaLimiteDaApi();
  }

  return false;
});

const obterEmpresaPorConfiguracao = async () => {
  if (!financasFeatureGateway) return;

  const response = await financasFeatureGateway.obterEmpresaPorConfiguracao(
    props.webservice?.id ?? ""
  );

  if (response?.Ok) {
    setValues({
      date: new Date().toISOString().split("T")[0],
      empresa: response.Result,
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoDePesquisa: ETipoDePesquisaCadis.None.toString()
    });
  }
};

onMounted(async () => {
  if (props.apiSelecionada?.requiresCompanyLookup) {
    try {
      await obterEmpresaPorConfiguracao();
    } catch (error) {
      console.error(error);
    }
  } else {
    setValues({
      date: new Date().toISOString().split("T")[0],
      empresa: props.webservice?.empresa ?? "",
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoDePesquisa: ETipoDePesquisaCadis.None.toString()
    });
  }
});

defineExpose({
  handleSubmit,
  errors,
  values,
  disabledSubmit
});
</script>

<style lang="scss" scoped></style>
